import React, { useCallback, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  styled,
} from '@mui/material';
import { useDropzone } from 'react-dropzone'
import axios from 'axios';
import moment from 'moment';

function parseInput(input) {
  const delimiter_row = /\r?\n|\r|\n/g
  const delimiter_col = /[,\t]/
  const rows = input.split(delimiter_row)
  const defaultHeader = ["Name", "Colony", "Date", "Description", "Concentration", "Volume"]
  const hasHeader = defaultHeader.filter(col => rows[0].includes(col)).length > 2
  const header = hasHeader ? rows.shift().split(delimiter_col) : defaultHeader
  const headerIndName = header.indexOf("Name")
  const headerIndColony = header.indexOf("Colony")
  const headerIndDate = header.indexOf("Date")
  const headerIndDescription = header.indexOf("Description")
  const headerIndConcentration = header.indexOf("Concentration")
  const headerIndVolume = header.indexOf("Volume")
  const parseRow = (row) => {
    const array = row.split(delimiter_col)
    return {
      name: headerIndName >= 0 ? array[headerIndName] : undefined,
      colony_id: headerIndColony >= 0 && array[headerIndColony] ? parseInt(array[headerIndColony]) : undefined,
      date: headerIndDate >= 0 && array[headerIndDate] ? array[headerIndDate] : undefined,
      description: headerIndDescription >= 0 ? array[headerIndDescription] : undefined,
      concentration: headerIndConcentration >= 0 && array[headerIndConcentration] ? parseFloat(array[headerIndConcentration]) : undefined,
      volume: headerIndVolume >= 0 && array[headerIndVolume] ? parseFloat(array[headerIndVolume]) : undefined,
    }
  }
  return rows.filter(row => row !== "").map(row => parseRow(row))
}

export default function ImportPlasmidsForm({open, onClose, onSubmit, showMessage, setData, ...props}) {
  const [input, setInput] = useState()
  const parsed = input ? parseInput(input) : []
  const handleSubmit = (event) => {
    event.preventDefault()
    onSubmit?.(event)
    onClose?.()
    // Convert units: concentration ng/μL → pg/μL (* 1000), volume μL → nL (* 1000)
    const toPost = parsed.map(p => ({
      ...p,
      concentration: p.concentration != null ? Math.round(p.concentration * 1000) : undefined,
      volume: p.volume != null ? Math.round(p.volume * 1000) : undefined,
      date: p.date ? moment(p.date).toISOString() : undefined,
    }))
    axios.post("/api/wetlab/plasmids/add", toPost)
      .then(({data : {ids}}) => {
        showMessage?.(`Saved ${ids.length} plasmid${ids.length === 1 ? '' : 's'}`)
        setData?.(prev => [...prev, ...parsed.map((plasmid, i) => ({...plasmid, id: ids[i] ?? -(i + 1)}))])
      })
      .catch(() => showMessage?.("Error saving plasmids"))
  }
  const TableCellNoWrap = styled(TableCell)({
    whiteSpace: "nowrap",
  })
  const onDrop = useCallback(acceptedFiles => {
    const reader = new FileReader();
    reader.onerror = () => showMessage?.("Error reading file.")
    reader.onload = (event) => setInput(event.target.result)
    acceptedFiles.forEach(file => {
      if (file.type.split("/")[0] !== "text")
        showMessage?.(`Can't parse files of type ${file.type}`)
      reader.readAsText(file)
    });
  // because showMessage deps
  // eslint-disable-next-line
  }, [])
  const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop: onDrop, noClick: true, noKeyboard: true})
  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md" {...props}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Import Batch of Plasmids</DialogTitle>
        <DialogContent>
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <TextField
              margin="dense"
              placeholder="Rows of Name, Colony, Date, Description, Concentration (ng/μL), Volume (μL)"
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              variant="outlined"
              value={input}
              onChange={e => setInput(e.target.value)}
              sx={{whiteSpace: "nowrap"}}
              focused={isDragActive || undefined}
            />
          </div>
          {parsed.length > 0 && <TableContainer><Table size="small">
            <TableHead>
              <TableRow>
                <TableCellNoWrap>Name</TableCellNoWrap>
                <TableCellNoWrap>Colony</TableCellNoWrap>
                <TableCellNoWrap>Date</TableCellNoWrap>
                <TableCellNoWrap>Description</TableCellNoWrap>
                <TableCellNoWrap>Concentration (ng/μL)</TableCellNoWrap>
                <TableCellNoWrap>Volume (μL)</TableCellNoWrap>
              </TableRow>
            </TableHead>
            <TableBody>
              {parsed.map((row, i) =>
                <TableRow key={i}>
                  <TableCellNoWrap>{row.name}</TableCellNoWrap>
                  <TableCellNoWrap>{row.colony_id}</TableCellNoWrap>
                  <TableCellNoWrap>{row.date}</TableCellNoWrap>
                  <TableCellNoWrap>{row.description}</TableCellNoWrap>
                  <TableCellNoWrap>{row.concentration}</TableCellNoWrap>
                  <TableCellNoWrap>{row.volume}</TableCellNoWrap>
                </TableRow>
              )}
            </TableBody>
          </Table></TableContainer>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={parsed.length === 0}>Add {parsed.length > 0 ? parsed.length : ''} Plasmid{parsed.length === 1 ? '' : 's'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
